/**
 * Receipt Contact Resolution Utility
 * 
 * Implements fallback logic for receipt contact information:
 * 1. Explicit receipt info entered manually by user
 * 2. Verified vehicle owner contact from registry
 * 3. Legacy session contact fields (compatibility)
 * 
 * This ensures verified vehicle owners receive receipts even when
 * they don't explicitly enter receipt information during the flow.
 */

/**
 * Resolve receipt contact information with fallback chain
 * 
 * Resolution order:
 * A. Explicit receipt info entered in the flow
 * B. Verified vehicle contact info from registry
 * C. Legacy session contact fields
 * 
 * @param {Object} session - Session state from SessionContext
 * @param {Object} session.receiptInfo - Explicit receipt info { fullName, email }
 * @param {Object} session.vehicle - Vehicle verification state
 * @param {boolean} session.vehicle.verified - Whether vehicle was verified
 * @param {string} session.vehicle.ownerName - Vehicle owner name from registry
 * @param {string} session.vehicle.ownerEmail - Vehicle owner email from registry
 * 
 * @returns {Object|null} Resolved contact { fullName, email } or null if none available
 */
export function resolveReceiptContact(session) {
  if (!session) {
    console.warn('[ReceiptContact] No session provided');
    return null;
  }

  // Enhanced debug logging to trace session state
  console.log('[ReceiptContact] 🔍 Resolving receipt contact with session state:', {
    hasReceiptInfo: !!session.receiptInfo,
    receiptEmail: session.receiptInfo?.email || null,
    hasVehicle: !!session.vehicle,
    vehicleVerified: session.vehicle?.verified || false,
    vehicleOwnerEmail: session.vehicle?.ownerEmail || null,
    vehicleOwnerName: session.vehicle?.ownerName || null,
    hasLegacyEmail: !!session.ownerEmail,
    legacyEmail: session.ownerEmail || null
  });

  // PRIORITY 1: Explicit receipt info entered in flow
  // This takes precedence - user explicitly provided their contact info
  if (session.receiptInfo?.email) {
    console.log('[ReceiptContact] ✅ Using explicit receipt info (Priority 1):', {
      email: session.receiptInfo.email,
      hasName: !!session.receiptInfo.fullName
    });
    
    return {
      fullName: session.receiptInfo.fullName || null,
      email: session.receiptInfo.email,
      source: 'explicit'
    };
  }

  // PRIORITY 2: Verified vehicle owner contact from registry
  // If vehicle was verified and has owner contact info, use it
  if (session.vehicle?.verified && session.vehicle?.ownerEmail) {
    console.log('[ReceiptContact] ✅ Using verified vehicle owner contact (Priority 2):', {
      email: session.vehicle.ownerEmail,
      hasName: !!session.vehicle.ownerName,
      vehicleId: session.vehicle.vehicleId,
      billingMode: session.vehicle.billingMode
    });
    
    return {
      fullName: session.vehicle.ownerName || null,
      email: session.vehicle.ownerEmail,
      source: 'vehicle_registry'
    };
  }

  // PRIORITY 3: Legacy session contact fields (compatibility fallback)
  // Check for any legacy contact fields that might exist
  if (session.ownerEmail) {
    console.log('[ReceiptContact] ✅ Using legacy session contact (Priority 3):', {
      email: session.ownerEmail,
      hasName: !!session.ownerName
    });
    
    return {
      fullName: session.ownerName || null,
      email: session.ownerEmail,
      source: 'legacy'
    };
  }

  // No contact information found in any source
  console.warn('[ReceiptContact] ❌ No receipt contact found in any source');
  console.warn('[ReceiptContact] Debug info:', {
    hasReceiptInfo: !!session.receiptInfo,
    receiptInfoKeys: session.receiptInfo ? Object.keys(session.receiptInfo) : [],
    hasVehicle: !!session.vehicle,
    vehicleKeys: session.vehicle ? Object.keys(session.vehicle) : [],
    vehicleVerified: session.vehicle?.verified,
    hasVehicleEmail: !!session.vehicle?.ownerEmail,
    vehicleEmail: session.vehicle?.ownerEmail,
    hasLegacyEmail: !!session.ownerEmail,
    allSessionKeys: Object.keys(session)
  });
  
  return null;
}

/**
 * Check if receipt contact information is available
 * Convenience wrapper for UI conditional rendering
 * 
 * @param {Object} session - Session state
 * @returns {boolean} True if contact is available
 */
export function hasReceiptContact(session) {
  const contact = resolveReceiptContact(session);
  return contact !== null && !!contact.email;
}

/**
 * Get receipt email only (no name)
 * Convenience wrapper for email-only operations
 * 
 * @param {Object} session - Session state
 * @returns {string|null} Email address or null
 */
export function getReceiptEmail(session) {
  const contact = resolveReceiptContact(session);
  return contact?.email || null;
}

/**
 * Get receipt display text for UI
 * Returns a user-friendly message about receipt delivery
 * 
 * @param {Object} session - Session state
 * @returns {Object} { hasContact: boolean, message: string, email: string|null }
 */
export function getReceiptDisplayInfo(session) {
  const contact = resolveReceiptContact(session);
  
  if (!contact || !contact.email) {
    return {
      hasContact: false,
      message: 'No receipt email on file.',
      email: null,
      source: null
    };
  }

  // Format message based on source
  let message;
  switch (contact.source) {
    case 'explicit':
      message = `Receipt will be sent to ${contact.email}`;
      break;
    case 'vehicle_registry':
      message = `Receipt will be sent to ${contact.email} (vehicle owner)`;
      break;
    case 'legacy':
      message = `Receipt will be sent to ${contact.email}`;
      break;
    default:
      message = `Receipt will be sent to ${contact.email}`;
  }

  return {
    hasContact: true,
    message,
    email: contact.email,
    fullName: contact.fullName,
    source: contact.source
  };
}
